import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { getCategoryMeta } from "@/lib/videoData";

interface VideoRowCardProps {
  video: Video;
}

export default function VideoRowCard({ video }: VideoRowCardProps) {
  const category = getCategoryMeta(video.category);
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group flex cursor-pointer gap-3 rounded-lg border border-background-200/90 bg-background-50 p-2.5 transition-colors hover:border-primary-200 hover:bg-primary-50/40 sm:gap-4 sm:p-3"
      aria-label={`查看 ${video.title} 详情`}
    >
      <div className="relative w-[104px] shrink-0 overflow-hidden rounded-md bg-primary-900 sm:w-[150px]">
        <div className="aspect-[2/3] w-full">
          <img
            src={video.cover}
            alt={`${video.title} 封面`}
            title={`${video.title} - ${category?.name ?? ""} - 糖豆视频`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
        <span className="absolute inset-0 flex items-center justify-center bg-primary-950/45 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500">
            <i className="ri-play-fill text-lg text-foreground-950" />
          </span>
        </span>
        <span className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded bg-primary-950/75 px-1.5 py-0.5 text-[10px] font-bold text-accent-300">
          <i className="ri-star-fill text-[9px]" />
          {video.rating.toFixed(1)}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 text-[15px] font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 sm:text-base">
            {video.title}
          </h3>
          <span className="hidden shrink-0 rounded bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900 sm:inline-block">
            {video.year}
          </span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-600 sm:text-xs">
          <span className="rounded bg-background-200/80 px-1.5 py-0.5 font-medium text-primary-700">
            {category?.name}
          </span>
          <span>{video.genre.join(" / ")}</span>
          <span className="text-background-400">|</span>
          <span>{video.region}</span>
          <span className="text-background-400">|</span>
          <span>{video.duration}</span>
        </div>

        <p className="mt-1.5 line-clamp-1 text-xs text-foreground-600">
          主演：{video.actors.join(" / ")}
        </p>
        <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">导演：{video.director}</p>

        <p className="mt-1.5 hidden line-clamp-2 text-xs leading-relaxed text-secondary-700 sm:block">
          {video.tagline} {video.description}
        </p>

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2">
          <span className="flex items-center gap-1 text-[11px] font-medium text-accent-700">
            <i className="ri-fire-line text-sm" />
            {(video.heat / 1000).toFixed(1)} 万热度
          </span>
          <span className="flex items-center gap-1 text-[11px] text-foreground-600">
            <i className="ri-refresh-line text-sm text-primary-500" />
            {video.status}
          </span>
          <span className="hidden text-[11px] text-foreground-500 sm:inline">更新于 {video.updatedAt}</span>
        </div>
      </div>
    </Link>
  );
}